import { http, upload, type UploadOptions } from '@/lib/http';
import type { DocumentDto } from './platform';
import type { DateOnly, DateRangeParams, PagedResult, RowVersion, UtcDateTime } from './types';

/**
 * Delivery (vận hành triển khai): projects, readiness checklist, tasks, acceptance, staff, resource bookings.
 * Mirrors apps/api/src/Crm.Api/Modules/Delivery (DeliveryModule.cs, ProjectService.cs, StaffService.cs).
 * Handover endpoints (/operations/handovers) are implemented by Commercial → see `handoverApi` in api/commercial.ts.
 */

// ---------- Permissions (DeliveryPerms) ----------
export const DP = {
  projectRead: 'delivery.project.read',
  projectReadOwn: 'delivery.project.read.own',
  projectManage: 'delivery.project.manage',
  staffManage: 'delivery.staff.manage',
  acceptanceManage: 'delivery.acceptance.manage',
} as const;

export const PROJECT_READ = [DP.projectRead, DP.projectReadOwn, DP.projectManage] as const;
export const STAFF_READ = [...PROJECT_READ, DP.staffManage] as const;
export const BOOKING_READ = [DP.projectRead, DP.projectManage, DP.staffManage] as const;

// ---------- Internal (non-catalog) enums ----------
export const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as const;
export const TASK_STATUS_LABEL: Record<string, string> = {
  TODO: 'Cần làm',
  IN_PROGRESS: 'Đang làm',
  DONE: 'Hoàn thành',
  CANCELLED: 'Đã hủy',
};
/** Allowed task transitions (TaskRules). */
export const TASK_TRANSITIONS: Record<string, string[]> = {
  TODO: ['IN_PROGRESS', 'DONE', 'CANCELLED'],
  IN_PROGRESS: ['DONE', 'TODO', 'CANCELLED'],
  DONE: ['IN_PROGRESS'],
  CANCELLED: [],
};
export const TASK_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export const TASK_PRIORITY_LABEL: Record<string, string> = { LOW: 'Thấp', NORMAL: 'Bình thường', HIGH: 'Cao', URGENT: 'Khẩn' };

export const ACCEPTANCE_STATUS_LABEL: Record<string, string> = { PENDING: 'Chờ xác nhận', ACCEPTED: 'Đã chấp nhận', REJECTED: 'Bị từ chối' };
export const ACCEPTANCE_SCOPE_LABEL: Record<string, string> = { ORDER: 'Toàn bộ đơn hàng', CLASS: 'Theo lớp', ITEM: 'Theo hạng mục' };

export const RESOURCE_TYPES = ['STAFF', 'ROOM', 'EQUIPMENT'] as const;
export const RESOURCE_TYPE_LABEL: Record<string, string> = { STAFF: 'Nhân sự', ROOM: 'Phòng', EQUIPMENT: 'Thiết bị' };
export const STAFF_STATUS_LABEL: Record<string, string> = { ACTIVE: 'Đang làm việc', INACTIVE: 'Ngừng' };

/** Error code for overlapping bookings (409; fieldErrors.bookings lists the clashing bookings). */
export const BOOKING_CONFLICT = 'RESOURCE_BOOKING_CONFLICT';

// ---------- Projects ----------
export interface ChecklistItem {
  code: string;
  label: string;
  done: boolean;
  doneBy: number | null;
  doneAtUtc: UtcDateTime | null;
  note: string | null;
}

export interface ProjectListItem {
  id: number;
  code: string;
  name: string;
  status: string;
  statusName: string;
  orderId: number | null;
  orderCode: string | null;
  customerId: number | null;
  ownerId: number | null;
  ownerName: string | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  openTaskCount: number;
  overdueTaskCount: number;
  createdAtUtc: UtcDateTime;
}

export interface ProjectDetail {
  id: number;
  code: string;
  name: string;
  status: string;
  statusName: string;
  isMain: boolean;
  orderId: number | null;
  orderCode: string | null;
  contractId: number | null;
  customerId: number | null;
  handoverId: number | null;
  salesOwnerUserId: number | null;
  ownerId: number | null;
  ownerName: string | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  actualStartAtUtc: UtcDateTime | null;
  actualEndAtUtc: UtcDateTime | null;
  readinessChecklist: ChecklistItem[];
  readinessComplete: boolean;
  holdReason: string | null;
  holdReviewOn: DateOnly | null;
  cancelReason: string | null;
  closedAtUtc: UtcDateTime | null;
  note: string | null;
  canClose: boolean;
  version: RowVersion;
  createdAtUtc: UtcDateTime;
}

export interface BoardColumn {
  status: string;
  statusName: string;
  count: number;
  items: ProjectListItem[];
}

export interface CreateProjectRequest {
  name: string;
  orderId?: number | null;
  ownerId?: number | null;
  plannedStart?: DateOnly | null;
  plannedEnd?: DateOnly | null;
  note?: string | null;
  additionalProject?: boolean;
}

export interface UpdateProjectRequest {
  name: string;
  ownerId: number | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  note: string | null;
  version: RowVersion;
}

export interface ProjectTransitionRequest {
  toStatus: string;
  reason?: string | null;
  holdReviewOn?: DateOnly | null;
  version: RowVersion;
}

// ---------- Tasks ----------
export interface TaskDto {
  id: number;
  projectId: number;
  parentTaskId: number | null;
  title: string;
  description: string | null;
  ownerId: number | null;
  ownerName: string | null;
  startAtUtc: UtcDateTime | null;
  dueAtUtc: UtcDateTime | null;
  status: string;
  priority: string;
  completedAtUtc: UtcDateTime | null;
  sortOrder: number;
  isOverdue: boolean;
  version: RowVersion;
}

export interface UpsertTaskRequest {
  title: string;
  description?: string | null;
  parentTaskId?: number | null;
  ownerId?: number | null;
  startAtUtc?: UtcDateTime | null;
  dueAtUtc?: UtcDateTime | null;
  status?: string | null;
  priority?: string | null;
  sortOrder?: number | null;
  version?: RowVersion | null;
}

// ---------- Acceptance ----------
export interface AcceptanceDto {
  id: number;
  projectId: number;
  orderId: number | null;
  scopeVersionId: number | null;
  scope: string;
  scopeRefId: number | null;
  status: string;
  acceptedBy: string | null;
  acceptedByTitle: string | null;
  acceptedAtUtc: UtcDateTime | null;
  reason: string | null;
  decidedBy: number | null;
  decidedAtUtc: UtcDateTime | null;
  note: string | null;
  evidence: DocumentDto[];
  version: RowVersion;
  createdAtUtc: UtcDateTime;
}

export interface CreateAcceptanceRequest {
  scope: string;
  scopeRefId?: number | null;
  scopeVersionId?: number | null;
  note?: string | null;
}

export interface AcceptanceDecisionRequest {
  decision: 'ACCEPT' | 'REJECT';
  acceptedBy?: string | null;
  acceptedByTitle?: string | null;
  acceptedAtUtc?: UtcDateTime | null;
  reason?: string | null;
  version: RowVersion;
}

// ---------- Staff & bookings ----------
export interface StaffDto {
  id: number;
  staffCode: string;
  name: string;
  userId: number | null;
  phone: string | null;
  email: string | null;
  skillTags: string[];
  isInstructor: boolean;
  status: string;
  version: RowVersion;
  /** Linked account (userId): display name + avatar, resolved by the API in the same query. */
  userName?: string | null;
  avatarUrl?: string | null;
}

export interface UpsertStaffRequest {
  staffCode: string;
  name: string;
  userId: number | null;
  phone: string | null;
  email: string | null;
  skillTags: string[];
  isInstructor: boolean;
  status: string | null;
  version: RowVersion | null;
}

export interface BookingDto {
  id: number;
  resourceType: string;
  resourceId: number;
  resourceName: string | null;
  projectId: number | null;
  classSessionId: number | null;
  startAtUtc: UtcDateTime;
  endAtUtc: UtcDateTime;
  status: string;
  note: string | null;
}

export interface BookingConflictPair {
  a: BookingDto;
  b: BookingDto;
}

export interface CreateBookingRequest {
  resourceType: string;
  resourceId: number;
  startAtUtc: UtcDateTime;
  endAtUtc: UtcDateTime;
  projectId?: number | null;
  note?: string | null;
}

/** Minimal class row (Training module, GET /training/classes?projectId=) for the project's class list. */
export interface ProjectClassRow {
  id: number;
  code: string;
  name: string;
  status: string;
  statusName: string;
  capacity: number;
  holdingCount: number;
  instructorName: string | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
}

const d = '/delivery';

export const deliveryApi = {
  // projects
  listProjects: (q: { q?: string; status?: string; ownerId?: number; orderId?: number; page?: number; pageSize?: number } & DateRangeParams) =>
    http.get<PagedResult<ProjectListItem>>(`${d}/projects`, { ...q }),
  board: (q: { ownerId?: number; perColumn?: number; q?: string } & DateRangeParams = {}) => http.get<BoardColumn[]>(`${d}/projects/board`, { ...q }),
  createProject: (req: CreateProjectRequest, key: string) => http.post<ProjectDetail>(`${d}/projects`, req, { idempotencyKey: key }),
  project: (id: number) => http.get<ProjectDetail>(`${d}/projects/${id}`),
  updateProject: (id: number, req: UpdateProjectRequest) => http.put<ProjectDetail>(`${d}/projects/${id}`, req),
  updateChecklist: (id: number, items: { code: string; done: boolean; note: string | null }[], version: RowVersion) =>
    http.put<ProjectDetail>(`${d}/projects/${id}/readiness-checklist`, { items, version }),
  transition: (id: number, req: ProjectTransitionRequest) => http.post<ProjectDetail>(`${d}/projects/${id}/transitions`, req),

  // tasks
  tasks: (projectId: number) => http.get<TaskDto[]>(`${d}/projects/${projectId}/tasks`),
  createTask: (projectId: number, req: UpsertTaskRequest) => http.post<TaskDto>(`${d}/projects/${projectId}/tasks`, req),
  updateTask: (projectId: number, taskId: number, req: UpsertTaskRequest) => http.put<TaskDto>(`${d}/projects/${projectId}/tasks/${taskId}`, req),
  workQueue: (q: { ownerId?: number; overdueOnly?: boolean } & DateRangeParams) => http.get<TaskDto[]>(`${d}/tasks`, { ...q }),

  // acceptance
  acceptances: (projectId: number) => http.get<AcceptanceDto[]>(`${d}/projects/${projectId}/acceptances`),
  createAcceptance: (projectId: number, req: CreateAcceptanceRequest) => http.post<AcceptanceDto>(`${d}/projects/${projectId}/acceptances`, req),
  uploadEvidence: (acceptanceId: number, file: File, title?: string, opts?: UploadOptions) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return upload<AcceptanceDto>(`${d}/acceptances/${acceptanceId}/evidence`, form, opts);
  },
  linkEvidence: (acceptanceId: number, documentId: number) =>
    http.post<AcceptanceDto>(`${d}/acceptances/${acceptanceId}/evidence-links`, { documentId }),
  decideAcceptance: (acceptanceId: number, req: AcceptanceDecisionRequest, key: string) =>
    http.post<AcceptanceDto>(`${d}/acceptances/${acceptanceId}/decisions`, req, { idempotencyKey: key }),

  // staff
  staff: (q: { q?: string; instructor?: boolean; status?: string } = {}) => http.get<StaffDto[]>(`${d}/staff`, { ...q }),
  staffMember: (id: number) => http.get<StaffDto>(`${d}/staff/${id}`),
  saveStaff: (id: number | null, req: UpsertStaffRequest) =>
    id ? http.put<StaffDto>(`${d}/staff/${id}`, req) : http.post<StaffDto>(`${d}/staff`, req),
  staffCalendar: (id: number, from: UtcDateTime, to: UtcDateTime) => http.get<BookingDto[]>(`${d}/staff/${id}/calendar`, { from, to }),

  // bookings
  bookings: (q: { resourceType?: string; resourceId?: number; from: UtcDateTime; to: UtcDateTime; includeCancelled?: boolean }) =>
    http.get<BookingDto[]>(`${d}/bookings`, { ...q }),
  conflicts: (from: UtcDateTime, to: UtcDateTime) => http.get<BookingConflictPair[]>(`${d}/bookings/conflicts`, { from, to }),
  createBooking: (req: CreateBookingRequest) => http.post<{ id: number }>(`${d}/bookings`, req),
  cancelBooking: (id: number, reason: string) => http.post<void>(`${d}/bookings/${id}/cancellations`, { reason }),

  // classes of a project (Training module)
  projectClasses: (projectId: number) =>
    http.get<PagedResult<ProjectClassRow>>('/training/classes', { projectId, pageSize: 100 }),
};

/** Query keys — first segment = domain. */
export const dk = {
  all: ['delivery'] as const,
  projects: ['delivery', 'projects'] as const,
  projectList: (p: object) => ['delivery', 'projects', 'list', p] as const,
  board: (p: object) => ['delivery', 'projects', 'board', p] as const,
  project: (id: number) => ['delivery', 'projects', 'detail', id] as const,
  tasks: (projectId: number) => ['delivery', 'projects', 'detail', projectId, 'tasks'] as const,
  acceptances: (projectId: number) => ['delivery', 'projects', 'detail', projectId, 'acceptances'] as const,
  projectClasses: (projectId: number) => ['training', 'classes', 'by-project', projectId] as const,
  workQueue: (p: object) => ['delivery', 'tasks', p] as const,
  staff: ['delivery', 'staff'] as const,
  staffList: (p: object) => ['delivery', 'staff', 'list', p] as const,
  bookings: ['delivery', 'bookings'] as const,
  bookingList: (p: object) => ['delivery', 'bookings', 'list', p] as const,
  conflicts: (p: object) => ['delivery', 'bookings', 'conflicts', p] as const,
};
